// src/hooks/sync/usePrayerPartners.js
// Prayer partners = app users linked through a prayer code.
//   partners  → people I pray for       (prayerPartners docs where fromUid == me)
//   followers → people who pray for me  (prayerPartners docs where toUid == me)
//   linked    → both lists merged by uid: everyone I am connected with, either direction
//   loaded    → true once a load has SUCCEEDED (callers must not treat "not loaded" as "no links")
// One-shot reads (no realtime listener) to keep Spark read usage tiny.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, getDocs, limit, query, where } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { QUERY_LIMIT } from '@/lib/storeKeys';

export function usePrayerPartners() {
  const { user } = useAuth();
  const uid = user?.uid && user.uid !== 'offline' ? user.uid : null;

  const [partners,  setPartners]  = useState([]); // [{ id, uid, name }]
  const [followers, setFollowers] = useState([]); // [{ id, uid, name }]
  const [loading,   setLoading]   = useState(false);
  const [loaded,    setLoaded]    = useState(false);

  const reload = useCallback(async () => {
    if (!uid || !db) { setPartners([]); setFollowers([]); setLoaded(false); return; }
    setLoading(true);
    try {
      const col = collection(db, 'prayerPartners');
      const [mine, theirs] = await Promise.all([
        getDocs(query(col, where('fromUid', '==', uid), limit(QUERY_LIMIT))),
        getDocs(query(col, where('toUid',   '==', uid), limit(QUERY_LIMIT))),
      ]);
      setPartners(mine.docs.map((d) => ({ id: d.id, uid: d.data().toUid, name: d.data().toName || 'Friend' })));
      setFollowers(theirs.docs.map((d) => ({ id: d.id, uid: d.data().fromUid, name: d.data().fromName || 'Friend' })));
      setLoaded(true);
    } catch (err) {
      console.warn('[usePrayerPartners] load failed:', err?.code || err);
    } finally {
      setLoading(false);
    }
  }, [uid]);

  useEffect(() => { reload(); }, [reload]);

  const linked = useMemo(() => {
    const byUid = new Map();
    [...partners, ...followers].forEach((p) => {
      if (p.uid && !byUid.has(p.uid)) byUid.set(p.uid, { uid: p.uid, name: p.name });
    });
    return [...byUid.values()];
  }, [partners, followers]);

  return { partners, followers, linked, loaded, loading, reload };
}

export default usePrayerPartners;